import React, { useEffect, useState, useRef } from "react";
import { Camera, X as XIcon } from "lucide-react";
import MessengerLayout from "../../layout/MessengerLayout";
import { toast } from "../../components/ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card  = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const input = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50";
const label = "text-xs text-[#00ff90]/60 uppercase tracking-wider block mb-1.5";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const STATUS_STYLE = {
  pending:         "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  pending_finance: "text-blue-400   bg-blue-500/10   border-blue-500/20",
  approved:        "text-[#00ff90]  bg-[#00ff90]/10  border-[#00ff90]/20",
  paid:            "text-[#00ff90]  bg-[#00ff90]/15  border-[#00ff90]/30",
  rejected:        "text-red-400    bg-red-500/10    border-red-500/20",
};

const fmtMoney = (n, c = "GBP") => `${c === "GBP" ? "£" : (c + " ")}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function ExpensesPage({ embedded = false } = {}) {
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } });
  const isManager = !!(user && (user.role === "manager" || user.role === "admin" || user.role === "superadmin" || user.roles?.includes?.("manager")));

  // Honour ?tab=team / ?tab=new from the URL (set by QuickManagerActions etc).
  const _initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t  = sp.get("tab");
      if (t === "team" && isManager) return "team";
      if (t === "new" || t === "mine") return t;
    } catch {}
    return "mine";
  })();
  const [tab, setTab]       = useState(_initialTab);

  const [mine, setMine]     = useState([]);
  const [team, setTeam]     = useState([]);
  const [draft, setDraft]   = useState({ title: "", amount: "", category: "other", expense_date: new Date().toISOString().slice(0, 10), description: "", receipt_url: "", receipt_filename: "" });
  const [busy, setBusy]     = useState(null);
  const [msg, setMsg]       = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptUploading,  setReceiptUploading]  = useState(false);
  const receiptInputRef = useRef(null);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setMsg({ ok: false, text: "Receipt must be under 10 MB" });
      toast.error("Receipt must be under 10 MB");
      return;
    }
    // Local preview while uploading
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setReceiptPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }
    setReceiptUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/expenses/upload-receipt`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setDraft((d) => ({ ...d, receipt_url: data.url, receipt_filename: data.filename || file.name }));
      toast.success("Receipt uploaded");
    } catch (err) {
      setReceiptPreview(null);
      setMsg({ ok: false, text: err.message });
      toast.error(err.message || "Receipt upload failed");
    } finally {
      setReceiptUploading(false);
      if (receiptInputRef.current) receiptInputRef.current.value = "";
    }
  };

  const clearReceipt = () => {
    setReceiptPreview(null);
    setDraft((d) => ({ ...d, receipt_url: "", receipt_filename: "" }));
  };

  const loadMine = async () => {
    const r = await fetch(`${API}/api/expenses/mine`, { headers: authH() });
    setMine(await r.json());
  };
  const loadTeam = async () => {
    const r = await fetch(`${API}/api/expenses/team`, { headers: authH() });
    setTeam(await r.json());
  };
  useEffect(() => { loadMine(); if (isManager) loadTeam(); }, []);   // eslint-disable-line

  const submit = async () => {
    setBusy("submit"); setMsg(null);
    try {
      const r = await fetch(`${API}/api/expenses`, {
        method: "POST", headers: authH(),
        body: JSON.stringify({ ...draft, amount: parseFloat(draft.amount) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Submit failed");
      setMsg({ ok: true, text: "Submitted" });
      toast.success("Claim submitted");
      setDraft({ title: "", amount: "", category: "other", expense_date: new Date().toISOString().slice(0, 10), description: "", receipt_url: "", receipt_filename: "" });
      setReceiptPreview(null);
      loadMine();
    } catch (e) { setMsg({ ok: false, text: e.message }); toast.error(e.message || "Failed to submit claim"); }
    finally { setBusy(null); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/expenses/${id}/approve`, { method: "PATCH", headers: authH(), body: JSON.stringify({}) });
      if (!r.ok) throw new Error("Failed");
      toast.success("Expense approved");
      loadTeam(); loadMine();
    } catch (e) {
      toast.error("Failed to approve expense");
    } finally { setBusy(null); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Rejection reason?"); if (!reason) return;
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/expenses/${id}/reject`, { method: "PATCH", headers: authH(), body: JSON.stringify({ reason }) });
      if (!r.ok) throw new Error("Failed");
      toast.success("Expense rejected");
      loadTeam(); loadMine();
    } catch (e) {
      toast.error("Failed to reject expense");
    } finally { setBusy(null); }
  };

  const Row = ({ c, actions }) => (
    <div className={`${card} p-4 flex items-start justify-between gap-4`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-white/85 truncate">{c.title}</p>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[c.status] || "text-white/40 bg-white/5 border-white/10"}`}>
            {c.status.replace(/_/g, " ")}
          </span>
          <span className="text-xs text-white/40 capitalize">· {c.category}</span>
        </div>
        {c.description && <p className="text-xs text-white/40 mt-1">{c.description}</p>}
        <p className="text-xs text-white/30 mt-1">Date: {c.expense_date} · Submitted {c.created_at?.slice(0, 10)}</p>
      </div>
      <div className="text-right shrink-0 flex flex-col items-end gap-2">
        <p className="text-base font-semibold text-white">{fmtMoney(c.amount, c.currency)}</p>
        {actions}
      </div>
    </div>
  );

  const Wrapper = embedded ? React.Fragment : MessengerLayout;
  return (
    <Wrapper>
      <div className={embedded ? "space-y-5" : "max-w-4xl mx-auto p-6 space-y-5"}>
        <header className="space-y-1">
          {!embedded && <h1 className="text-xl font-semibold text-white">Expenses</h1>}
          <p className="text-xs text-white/40">Submit claims and track approval status.</p>
        </header>

        <div className="flex gap-2">
          <button onClick={() => setTab("mine")}    className={`text-xs px-3 py-1.5 rounded-full border ${tab === "mine"   ? "border-[#00ff90]/50 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/50"}`}>My claims</button>
          <button onClick={() => setTab("new")}     className={`text-xs px-3 py-1.5 rounded-full border ${tab === "new"    ? "border-[#00ff90]/50 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/50"}`}>Submit new</button>
          {isManager && <button onClick={() => setTab("team")} className={`text-xs px-3 py-1.5 rounded-full border ${tab === "team" ? "border-[#00ff90]/50 bg-[#00ff90]/10 text-[#00ff90]" : "border-white/10 text-white/50"}`}>Team pending ({team.length})</button>}
        </div>

        {tab === "mine" && (
          <div className="space-y-2">
            {mine.length === 0 ? <p className="text-white/40 text-sm italic p-3">No claims yet.</p>
              : mine.map(c => <Row key={c.id} c={c} />)}
          </div>
        )}

        {tab === "new" && (
          <div className={`${card} p-5 space-y-3`}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className={label}>Title</label>
                <input className={input} value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} /></div>
              <div><label className={label}>Amount (£)</label>
                <input type="number" step="0.01" className={input} value={draft.amount} onChange={e => setDraft(d => ({ ...d, amount: e.target.value }))} /></div>
              <div><label className={label}>Category</label>
                <select className={input} value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}>
                  <option value="travel">Travel</option><option value="meals">Meals</option>
                  <option value="equipment">Equipment</option><option value="other">Other</option>
                </select></div>
              <div><label className={label}>Expense date</label>
                <input type="date" className={input} value={draft.expense_date} onChange={e => setDraft(d => ({ ...d, expense_date: e.target.value }))} /></div>
              <div>
                <label className={label}>Receipt</label>
                <input
                  ref={receiptInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={handleReceiptUpload}
                  style={{ display: "none" }}
                />
                {!draft.receipt_url ? (
                  <button
                    type="button"
                    onClick={() => receiptInputRef.current?.click()}
                    disabled={receiptUploading}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#00ff90]/30 bg-[#00ff90]/5 text-[#00ff90]/80 hover:bg-[#00ff90]/10 transition-colors text-xs disabled:opacity-50"
                  >
                    <Camera size={14} />
                    {receiptUploading ? "Uploading…" : "Take photo / Upload receipt"}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 rounded-lg border border-[#00ff90]/30 bg-black/40 p-2">
                    {receiptPreview ? (
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                      />
                    ) : (
                      <div style={{
                        width: 40, height: 40, borderRadius: 6,
                        background: "rgba(0,255,144,0.1)", color: "#00FF99",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, flexShrink: 0,
                      }}>PDF</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="text-xs text-white/70 truncate">{draft.receipt_filename || "Receipt"}</p>
                      <p className="text-[10px] text-white/30 truncate">{draft.receipt_url}</p>
                    </div>
                    <button
                      type="button"
                      onClick={clearReceipt}
                      title="Remove receipt"
                      className="text-white/30 hover:text-red-400 flex-shrink-0"
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="col-span-2"><label className={label}>Description</label>
                <textarea rows={3} className={input} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} /></div>
            </div>
            <button onClick={submit} disabled={busy === "submit"}
              className="text-xs px-4 py-2 rounded-lg border border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">
              {busy === "submit" ? "Submitting…" : "Submit claim"}
            </button>
            {msg && <p className={`text-xs ${msg.ok ? "text-[#00ff90]" : "text-red-400"}`}>{msg.text}</p>}
          </div>
        )}

        {tab === "team" && isManager && (
          <div className="space-y-2">
            {team.length === 0 ? <p className="text-white/40 text-sm italic p-3">No team claims awaiting approval.</p>
              : team.map(c => <Row key={c.id} c={c}
                  actions={
                    <div className="flex gap-2">
                      <button onClick={() => approve(c.id)} disabled={busy === c.id}
                        className="text-xs px-3 py-1 rounded border border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">Approve</button>
                      <button onClick={() => reject(c.id)} disabled={busy === c.id}
                        className="text-xs px-3 py-1 rounded border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20">Reject</button>
                    </div>
                  } />)}
          </div>
        )}
      </div>
    </Wrapper>
  );
}
